/* SSi website – offer-test variant configs (Design handoff 2026-06-11).
 *
 * Four alternative offers for the 7-day-free-trial funnel, ported from the
 * static handoff (static/index-v*.html etc.). Each variant carries:
 *
 *   tweaks.shared     – key overrides applied on every page that takes them
 *                       (coach modal CTA, the brand-red footer CTA heading
 *                       keeps the page's own footerCta key)
 *   tweaks.home       – overrides merged into routes/Home.jsx defaults
 *   tweaks.learnWelsh – overrides merged into the Welsh /learn/:lang defaults
 *   tweaks.pricing    – overrides merged into routes/Pricing.jsx defaults
 *   tweaks.pay        – overrides merged into routes/Pay.jsx defaults
 *   aside             – the Sample section's subscription aside (replaces the
 *                       hardcoded 7-day-trial pitch in components/Sample.jsx)
 *   footerCta         – the brand-red Footer CTA block price + button + note
 *   learnHero         – language-neutral CTA/trust/FAQ copy for the nine
 *                       non-Welsh /learn/:lang heroes (Welsh uses learnWelsh)
 *   pay               – behaviour flags + Stripe product ids for /pay
 *
 * Copy is verbatim from the handoff's per-variant T objects – only the
 * static-prototype plumbing keys (payDemo*, *PayBase, ofcHref) are dropped,
 * because containment in the SPA comes from OfferContext state, not from
 * rewriting hrefs.
 *
 * The productIds below are backend product-definition ids (each carries the
 * Stripe price id where relevant plus a `type` field), confirmed wired up on
 * Dev by Ivan 2026-06-12. All four POST to the same endpoint:
 *   POST /membership/onestep/:product_id   (c & s as query params)
 *   v1: plain subscriptions, no trial (charge today, 30-day refund promise)
 *       → standard-month-15-gbp / standard-year-144-gbp
 *   v2: Stripeless immediate access – product def says no checkout required.
 *       POST with `email` in the BODY (c/s stay query params); returns
 *       response_type:"direct" + redirect_url to send the user to.
 *       → oneoff-month-trial
 *   v3: single £1 one-off payment, NO saved mandate / subscription
 *       → oneoff-month-1-gbp
 *   v4: standard £15/mo subscription + £14-off duration=once coupon
 *       (customer pays £1 in month 1; invoice shows £15 − £14 promo) +
 *       guaranteed day-before-renewal reminder email. Coupon route agreed
 *       with Ivan 2026-06-12 — no Billing API migration. The coupon is
 *       pitched as a feature on every v4 page ("Apply my coupon – 93% off").
 *       → standard-month-15-gbp-discount14
 *
 * v1/v3/v4 return `secret` plus a new response_type:"stripe" field (ignored).
 * The email reminder cronjob for v4 is still pending on Ivan's side.
 */

export const OFFER_STORAGE_KEY = 'ssi_offer';

export const OFFER_VARIANTS = {

  /* ───────────────────────── v1 – straight to payment, annual-first ── */
  v1: {
    pay: {
      showPlans: true,
      showCard:  true,
      defaultPlan: 'annual',
      productIds: {
        monthly: 'standard-month-15-gbp',  // TODO(Ivan): real no-trial product
        annual:  'standard-year-144-gbp',  // TODO(Ivan): real no-trial product
      },
      planMonthly: { price: '£15',  per: '/month' },
      planAnnual:  { price: '£12',  per: '/month · £144 billed yearly' },
    },
    aside: {
      defaultBilling: 'annual',
      toggle: true,
      savePill: 'Save £36',
      monthly: { curr: '£', num: '15', per: '/month', note: 'or £12 a month if you pay for the year' },
      annual:  { curr: '£', num: '12', per: '/month', note: 'billed as £144 once a year' },
      trialStrong: '30-day refund, no questions asked.',
      trialRest: 'If it’s not for you, tell us within 30 days and we’ll refund you in full – whichever plan you chose.',
      perks: ['All 11 courses included', 'Cancel any time, no contract'],
      perkAnnual: '£36 less than paying monthly',
      perkMonthly: 'Switch to annual whenever',
      cta: 'Start speaking today',
      fine: 'Full access from the moment you pay. Change your mind within 30 days and we’ll refund you in full.',
    },
    footerCta: {
      curr: '£', num: '12', per: '/month',
      cta: 'Start speaking today',
      note: 'Paid yearly at £144 – or £15 a month if you’d rather stay flexible. 30-day no-questions refund on both.',
    },
    // Offer copy for the nine non-Welsh /learn/:lang heroes + their FAQ cost
    // answer. Welsh has its own per-key overrides in tweaks.learnWelsh; the
    // others share prefixed Hero components, so LearnLang maps these
    // language-neutral values onto the active slug's Hero keys.
    learnHero: {
      cta:   'Start speaking today',
      trust: 'Over 100,000 learners · £12 a month paid yearly (£144), or £15 monthly · 30-day no-questions refund',
      faqA7: 'It’s £12 a month if you pay for a year (£144), or £15 a month if you’d rather stay flexible. Both come with a no-questions-asked refund in your first 30 days.',
    },
    tweaks: {
      shared: {
        coachSentLede: 'Aran will get back to you within two working days. In the meantime, feel free to get started today – if you end up booking coaching, we’ll credit it.',
        coachSentCta: 'Start speaking today',
      },
      home: {
        heroCta1: 'Start speaking today',
        faqA7: 'It’s £12 a month if you pay for a year (£144), or £15 a month if you’d rather stay flexible. Both come with a no-questions-asked refund in your first 30 days.',
      },
      learnWelsh: {
        cyHeroCta1: 'Start speaking Welsh today',
        cyHeroTrust: 'Tens of thousands of Welsh speakers have started here · £12 a month paid yearly (£144), or £15 monthly · 30-day no-questions refund',
        faqA7: 'It’s £12 a month if you pay for a year (£144), or £15 a month if you’d rather stay flexible. Both come with a no-questions-asked refund in your first 30 days.',
        cyClosingCta1: 'Start speaking Welsh today',
        cyFlowKicker: "What happens when you click 'Start speaking Welsh today'",
        cyFlowStep1Title: 'Choose monthly or annual.',
        cyFlowStep1Body: '£12 a month if you pay for the year (£144), or £15 a month if you’d rather stay flexible. Full access from the moment you pay.',
        cyFlowStep3Title: 'Change your mind? No questions.',
        cyFlowStep3Body: 'If it’s not for you, tell us within 30 days and we’ll refund you in full – whichever plan you chose. We’d rather have your honest ‘no thanks’ than your reluctant subscription.',
      },
      pricing: {
        planMonthlyCta: 'Get started – £15/month',
        planAnnualBlurb: 'The committed option. £12 a month when you pay for the year.',
        planAnnualCompare: '£144 billed once a year – save £36',
        planAnnualCta: 'Get started – £12/month',
        planSaveBadge: 'Save £36',
        planAnnualNum: '12',
        planAnnualPer: '/ month',
        planLine2: '30-day no-questions-asked refund on both plans',
        trialStripStrong: 'Changed your mind?',
        trialStripRest: 'Tell us within 30 days and we’ll refund you in full – no questions asked, on either plan.',
        promiseBody: 'We’re always improving the app, and we do sometimes increase the price for new subscribers. But if you sign up today at £12 a month (paid yearly) or £15 a month, that’s your price – locked in for as long as you keep your subscription going. If your price ever goes up, it’ll be because you cancelled and came back.',
        pfaqQ1: 'Is the 30-day refund really no questions asked?',
        pfaqA1: 'Yes. Pay today, and if it’s not for you tell us within 30 days – monthly or annual – and we’ll refund you in full. We’d rather have your honest feedback than your reluctant money.',
      },
      pay: {
        payKicker: 'Start speaking today',
        payLede: 'Four quick steps, then you’re in. Your subscription starts today – and if it’s not for you, tell us within 30 days and we’ll refund you in full.',
        payStep2Sub: '£12 a month if you pay for the year, or £15 a month if you’d rather stay flexible. Switch whenever you like.',
        payPlanAnnualBlurb: 'The committed option. £144 once a year – £36 less than paying monthly.',
        payPlanAnnualSave: 'Save £36',
        payStep4Sub: 'Securely handled by Stripe. Your plan starts today – with a 30-day no-questions refund if you change your mind.',
        payCta: 'Pay & start speaking',
        payCtaFine: 'By subscribing you agree to our Terms and Privacy Policy. If it’s not for you, tell us within 30 days and we’ll refund you in full – no questions asked.',
        paySummaryFor: 'Your subscription to',
        paySummaryNoLang: 'Your subscription',
        payTl1Body: 'Instant access to all 11 courses – your subscription starts now.',
        payTl2Title: 'Your first 30 days',
        payTl2Body: 'If it’s not working for you, tell us and we’ll refund you in full. No questions asked, monthly or annual.',
        payTl3TitleM: 'Monthly – £15 a month',
        payTl3BodyM: 'Billed on the same date each month. Cancel any time and billing stops at the end of the month.',
        payTl3TitleA: 'Annual – £144 a year',
        payTl3BodyA: 'That’s £12 a month. Price locked in for life as long as your subscription stays active.',
        payTl3PriceM: '',
        payTl3PriceA: '',
      },
    },
  },

  /* ───────────────────────────── v2 – 30 days free, no card details ── */
  v2: {
    pay: {
      showPlans: false,
      showCard:  false,
      defaultPlan: 'monthly',
      // Stripeless immediate-access flow – product def requires no checkout.
      // PayNoCardStart POSTs onestep/oneoff-month-trial with email in the body
      // and follows the returned redirect_url.
      productIds: {
        monthly: 'oneoff-month-trial',
        annual:  'oneoff-month-trial',
      },
    },
    aside: {
      defaultBilling: 'monthly',
      toggle: false,
      static: { curr: '', num: 'Free', per: 'for 30 days', note: 'then from £15 a month – only if you choose to carry on' },
      trialStrong: 'No card details.',
      trialRest: 'Full access to every language and every level for 30 days. Nothing to cancel, because there’s nothing to charge.',
      perks: ['All 11 courses – every level unlocked', 'No card, no auto-charge, no small print', 'Keep your progress if you subscribe later'],
      cta: 'Start your free 30 days',
      fine: 'After 30 days, pick a plan if you want to carry on. Nothing happens automatically.',
    },
    footerCta: {
      curr: '', num: 'Free', per: 'for 30 days',
      cta: 'Start your free 30 days',
      note: 'Every language, every level – no card details. After 30 days, carry on from £15 a month only if you choose to.',
    },
    learnHero: {
      cta:   'Start your free 30 days',
      trust: 'Over 100,000 learners · 30 days free · every language, every level · no card details',
      faqA7: 'Your first 30 days are completely free – every language, every level, no card details. After that it’s £15 a month (or £150 a year) if you want to carry on. And you can cancel any time.',
    },
    tweaks: {
      shared: {
        coachSentLede: 'Aran will get back to you within two working days. In the meantime, feel free to start your free 30 days and get a head start – if you end up booking coaching, we’ll credit it.',
        coachSentCta: 'Start your free 30 days',
      },
      home: {
        heroCta1: 'Start your free 30 days',
        faqA7: 'Your first 30 days are completely free – every language, every level, and we don’t ask for a card. After that it’s £15 a month (or £150 a year) if you want to carry on. And you can cancel any time.',
      },
      learnWelsh: {
        cyHeroCta1: 'Start your free 30 days',
        cyHeroTrust: 'Tens of thousands of Welsh speakers have started here · 30 days free · every language, every level · no card details',
        faqA7: 'Your first 30 days are completely free – every language, every level, no card details. After that it’s £15 a month (or £150 a year) if you want to carry on. And you can cancel any time.',
        cyClosingCta1: 'Start your free 30 days',
        cyFlowKicker: "What happens when you click 'Start your free 30 days'",
        cyFlowStep1Title: 'Start your free 30 days.',
        cyFlowStep1Body: 'No card details, no payment – just an email address. Every language, every level, no limits, the whole time.',
        cyFlowStep3Title: 'Decide on day 30.',
        cyFlowStep3Body: 'Love it, and you pick a plan from £15 a month. Not for you, and you simply stop – there’s nothing to cancel, because we never took a card.',
      },
      pricing: {
        planMonthlyCta: 'Try free for 30 days',
        planAnnualCta: 'Try free for 30 days',
        planLine2: 'Your first 30 days are free – no card details needed',
        trialStripStrong: '30 days free',
        trialStripRest: 'on both plans. Full access to every language and every level, no card details, nothing to cancel. If you love it, pick a plan; if not, simply stop.',
        pfaqQ1: 'Is the 30-day trial really free?',
        pfaqA1: 'Yes. Full access to every course and every level for 30 days – and we never ask for a card. If you do nothing, nothing happens. There’s no ‘oops’ first-month charge, because there’s no card to charge.',
      },
      pay: {
        payKicker: 'Start your free 30 days',
        payLede: 'Two quick steps, then you’re in. No card details, no payment – your first 30 days are completely free, every language, every level.',
        payStep1Sub: 'Your trial unlocks all of them – this is just where you’ll start.',
        payCta: 'Start my free 30 days',
        payCtaFine: 'By starting your trial you agree to our Terms and Privacy Policy. No card details, no charges – and nothing to cancel if you decide it’s not for you.',
        paySummaryFor: 'Your free 30 days of',
        paySummaryNoLang: 'Your free 30 days',
        payTl1Body: 'Instant access to all 11 courses, every level. No card details, no payment.',
        payTl2Title: 'Days 1–30',
        payTl2Body: 'The full app, no limits. Learn as gently or as intensively as you like.',
        payTl3TitleM: 'Day 30 – your call',
        payTl3BodyM: 'Want to carry on? Pick a plan from £15 a month. If not, you don’t need to do anything – we never took a card, so there’s nothing to cancel.',
        payTl3PriceM: '',
      },
    },
  },

  /* ─────────────────── v3 – £1 for 30 days, NO automatic subscription ── */
  v3: {
    pay: {
      showPlans: false,
      showCard:  true,
      defaultPlan: 'monthly',
      productIds: {
        monthly: 'oneoff-month-1-gbp', // single £1 one-off payment, no mandate
        annual:  'oneoff-month-1-gbp',
      },
    },
    aside: {
      defaultBilling: 'monthly',
      toggle: false,
      static: { curr: '£', num: '1', per: 'for 30 days', note: 'one-off – no subscription attached' },
      trialStrong: 'No automatic subscription.',
      trialRest: '£1 is the only charge we’ll take. Day 31 costs you nothing unless you actively sign up.',
      perks: ['All 11 courses – every level unlocked', 'One-off £1 – nothing renews by itself', 'Subscribe from £15 a month if you love it'],
      cta: 'Try 30 days for £1',
      fine: 'We don’t store a subscription against your card. If you love it, subscribing is a choice you make – never something that happens to you.',
    },
    footerCta: {
      curr: '£', num: '1', per: 'for 30 days',
      cta: 'Try 30 days for £1',
      note: 'One pound, one month, everything unlocked – and no automatic subscription. Day 31 costs you nothing unless you sign up.',
    },
    learnHero: {
      cta:   'Try 30 days for £1',
      trust: 'Over 100,000 learners · 30 days of everything for £1 · no automatic subscription',
      faqA7: 'Your first 30 days cost £1 – and there’s no automatic subscription. If you love it, you can subscribe for £15 a month (or £150 a year). If you don’t, day 31 costs you nothing.',
    },
    tweaks: {
      shared: {
        coachSentLede: 'Aran will get back to you within two working days. In the meantime, feel free to start your £1 month and get a head start – if you end up booking coaching, we’ll credit it.',
        coachSentCta: 'Try 30 days for £1',
      },
      home: {
        heroCta1: 'Try 30 days for £1',
        faqA7: 'Your first 30 days cost £1 – and there’s no automatic subscription. If you love it, you can subscribe for £15 a month (or £150 a year). If you don’t, day 31 costs you nothing.',
      },
      learnWelsh: {
        cyHeroCta1: 'Try 30 days for £1',
        cyHeroTrust: 'Tens of thousands of Welsh speakers have started here · 30 days of everything for £1 · no automatic subscription',
        faqA7: 'Your first 30 days cost £1 – and there’s no automatic subscription. If you love it, you can subscribe for £15 a month (or £150 a year). If you don’t, day 31 costs you nothing.',
        cyClosingCta1: 'Try 30 days for £1',
        cyFlowKicker: "What happens when you click 'Try 30 days for £1'",
        cyFlowStep1Title: 'Start your 30 days for £1.',
        cyFlowStep1Body: 'One pound, once. The full course, every level, no limits – and we don’t set up a subscription. £1 is the only charge we’ll take.',
        cyFlowStep3Title: 'Day 31 – it’s your call.',
        cyFlowStep3Body: 'Nothing renews by itself. If you love it, subscribe at £15 a month from your account. If not, your card is never touched again.',
      },
      pricing: {
        planMonthlyCta: 'Try 30 days for £1',
        planAnnualCta: 'Try 30 days for £1',
        planLine2: '£1 gets you your first 30 days – no automatic subscription',
        trialStripStrong: '£1 for 30 days',
        trialStripRest: 'of full access – and no automatic subscription. £1 is the only charge we’ll take; day 31 costs you nothing unless you actively sign up.',
        pfaqQ1: 'Does the £1 trial roll into a subscription?',
        pfaqA1: 'No – and that’s deliberate. Your £1 buys 30 days, full stop. We don’t store a subscription against your card, so day 31 costs you nothing unless you actively sign up.',
      },
      pay: {
        payKicker: '30 days for £1',
        payLede: 'Three quick steps, then you’re in. £1 buys your first 30 days – and we don’t set up a subscription. Day 31 costs you nothing unless you choose it.',
        payStep1Sub: 'Your trial unlocks all of them – this is just where you’ll start.',
        payStep4Sub: 'Securely handled by Stripe. One charge of £1 today. We don’t set up a subscription – £1 is the only charge we’ll take.',
        payCta: 'Start my 30 days – £1',
        payCtaFine: 'By starting your trial you agree to our Terms and Privacy Policy. £1 is a one-off charge – no subscription is set up, and nothing renews automatically.',
        paySummaryFor: 'Your £1 month of',
        paySummaryNoLang: 'Your £1 month',
        payTl1Title: 'Today – £1',
        payTl1Body: 'One charge of £1, then instant access to all 11 courses, every level.',
        payTl2Title: 'Days 1–30',
        payTl2Body: 'The full app, no limits – and no subscription ticking away in the background.',
        payTl3TitleM: 'Day 31 – nothing happens',
        payTl3BodyM: 'That’s the point. Nothing renews by itself. If you love it, subscribe from £15 a month; if not, your card is never touched again.',
        payTl3PriceM: '',
      },
    },
  },

  /* ── v4 – 93%-off coupon on month 1 (£1), then £15/month, reminder email ── */
  v4: {
    pay: {
      showPlans: false,
      showCard:  true,
      defaultPlan: 'monthly',
      productIds: {
        // standard £15/mo subscription + £14-off duration=once coupon applied
        // at Checkout Session creation. Reminder email fires the day before
        // renewal (calendar month, not a fixed day 29) — cronjob pending Ivan.
        monthly: 'standard-month-15-gbp-discount14',
        annual:  'standard-month-15-gbp-discount14',
      },
    },
    aside: {
      defaultBilling: 'monthly',
      toggle: false,
      static: { curr: '£', num: '1', per: 'for your first month', note: 'your coupon takes 93% off the usual £15 – then £15 a month' },
      trialStrong: 'We’ll email you before month 2',
      trialRest: '– the day before your first full charge – so there are no surprises. Cancel before then and the £1 is all you’ll ever pay.',
      perks: ['All 11 courses – every level unlocked', 'Coupon applied automatically – no code needed', 'Reminder email before your first full charge'],
      cta: 'Apply my coupon – 93% off',
      fine: 'Your coupon takes 93% off month 1 – £1 instead of £15, then £15 a month. We’ll remind you the day before your first full charge – no surprises.',
    },
    footerCta: {
      curr: '£', num: '1', per: 'for your first month',
      cta: 'Apply my coupon – 93% off',
      note: 'Your coupon takes 93% off the usual £15 – then £15 a month from month 2. We’ll email you the day before your first full charge – no surprises.',
    },
    learnHero: {
      cta:   'Apply my coupon – 93% off',
      trust: 'Over 100,000 learners · 93% off your first month with your coupon – £1 instead of £15 · reminder email before your first full charge',
      faqA7: 'Right now there’s a coupon for 93% off your first month – £1 instead of £15, applied automatically at checkout. After that it’s £15 a month. We’ll email you the day before your first full charge so there are no surprises – and you can cancel any time.',
    },
    tweaks: {
      shared: {
        coachSentLede: 'Aran will get back to you within two working days. In the meantime, feel free to apply your coupon and get a head start – if you end up booking coaching, we’ll credit it.',
        coachSentCta: 'Apply my coupon – 93% off',
      },
      home: {
        heroCta1: 'Apply my coupon – 93% off',
        faqA7: 'Right now there’s a coupon for 93% off your first month – £1 instead of £15, applied automatically at checkout. After that it’s £15 a month. We’ll email you the day before your first full charge so there are no surprises – and you can cancel any time.',
      },
      learnWelsh: {
        cyHeroCta1: 'Apply my coupon – 93% off',
        cyHeroTrust: 'Tens of thousands of Welsh speakers have started here · 93% off your first month with your coupon – £1 instead of £15 · reminder email before your first full charge',
        faqA7: 'Right now there’s a coupon for 93% off your first month – £1 instead of £15, applied automatically at checkout. After that it’s £15 a month. We’ll email you the day before your first full charge so there are no surprises – and you can cancel any time.',
        cyClosingCta1: 'Apply my coupon – 93% off',
        cyFlowKicker: "What happens when you click 'Apply my coupon – 93% off'",
        cyFlowStep1Title: 'Your coupon comes off first.',
        cyFlowStep1Body: 'It’s applied automatically at checkout – 93% off your first month, so you pay £1 instead of £15. Full course, every level, no limits, from the moment you pay.',
        cyFlowStep3Title: 'Before month 2 – we email you first.',
        cyFlowStep3Body: 'The day before your first full charge we’ll send a reminder, so there are no surprises. Cancel in two clicks and the £1 is all you’ve spent.',
      },
      pricing: {
        planMonthlyCta: 'Apply my coupon – 93% off',
        planAnnualCta: 'Apply my coupon – 93% off',
        planLine2: '93% off your first month with your coupon – £1 instead of £15',
        trialStripStrong: '93% off month 1',
        trialStripRest: '– your coupon brings your first month down from £15 to £1, applied automatically at checkout. Then £15 a month, with a reminder email the day before your first full charge.',
        pfaqQ1: 'How does the 93% off coupon work?',
        pfaqA1: 'It’s applied automatically at checkout – no code to type. The coupon takes £14 off your first month, so you pay £1 today instead of £15; your receipt shows the full price and the discount. After that it’s £15 a month – and we’ll email you the day before your first full charge, so there are no surprises. Cancel before then and £1 is all you’ve spent.',
      },
      pay: {
        payKicker: '93% off your first month',
        payLede: 'Three quick steps, then you’re in. Your coupon takes 93% off your first month – £1 instead of £15 – then it’s £15 a month, with a reminder email the day before your first full charge.',
        payCouponStrong: 'Coupon applied',
        payCouponRest: '93% off your first month – you pay £1 today instead of £15.',
        payStep1Sub: 'Your first month unlocks all of them – this is just where you’ll start.',
        payStep4Sub: 'Securely handled by Stripe. Your coupon is already applied – £1 today instead of £15. Then £15 a month unless you cancel; we’ll email you the day before your first full charge.',
        payCta: 'Start my first month – £1',
        payCtaFine: 'By subscribing you agree to our Terms and Privacy Policy. Your coupon takes £14 off today’s charge – your receipt shows the full £15 price with the discount. Then £15 a month; we’ll email you the day before your first full charge.',
        paySummaryFor: 'Your £1 first month of',
        paySummaryNoLang: 'Your £1 first month',
        payTl1Title: 'Today – £1',
        payTl1Body: 'Your coupon takes £14 off – you pay £1, and all 11 courses unlock instantly, every level.',
        payTl2Title: 'The day before month 2 – we email you',
        payTl2Body: 'A reminder that your first full month starts tomorrow, with a one-click cancel link. No surprise charges – ever.',
        payTl3TitleM: 'Month 2 – £15 a month begins',
        payTl3PriceM: '',
      },
    },
  },
};
